import { useLayoutEffect, useState } from "react";

type VisualViewportRect = { top: number; height: number };

/**
 * The part of the page that is actually on screen, tracked while `enabled`.
 *
 * A `position: fixed` overlay is laid out against the layout viewport, which on
 * iOS is not what the guest sees: the browser's chrome, and the "< Messages"
 * strip when the page is opened from another app, cover part of it. Pinning an
 * overlay to this rect instead keeps its bottom edge on the visible bottom.
 * null when disabled or where visualViewport is unsupported — callers keep
 * their CSS layout then.
 */
export const useVisualViewportRect = (
  enabled: boolean
): VisualViewportRect | null => {
  const [rect, setRect] = useState<VisualViewportRect | null>(null);

  useLayoutEffect(() => {
    const viewport =
      typeof window === "undefined" ? undefined : window.visualViewport;

    if (!enabled || !viewport) {
      return;
    }

    const update = () =>
      setRect({ top: viewport.offsetTop, height: viewport.height });

    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);

    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, [enabled]);

  return enabled ? rect : null;
};
